import type React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { PromotionPlacementZod } from '@onr/schema'
import { useParams } from '@tanstack/react-router'

import { usePromotionPlanExport } from '@/api/hooks/usePromotionPlanExport'
import { useUpdatePromotionPlan } from '@/api/useUpdatePromotionPlan'
import { Toaster } from '@/components/molecules/Toaster'
import { toast } from '@/components/molecules/Toaster/useToast'
import {
  AdminPromotionPlacementsView,
  type AdminPromotionPlacementsViewProps,
} from '@/components/templates/AdminPromotionPlacementsView/AdminPromotionPlacementsView.tsx'
import { downloadFromBase64 } from '@/lib/download'
import { useBackToPilote } from '@/pages/actions/useBackToPilote'
import { useRegionList } from '@/state/regions/useRegionList'

export const AdminPromotionPlacementsPage: React.FC = () => {
  const intl = useIntl()
  const { initiativeId } = useParams({ from: '/promotions/$initiativeId/placements' })
  const regions = useRegionList()

  const regionsWithPlacements = useMemo(
    () =>
      regions.data.map((region) => ({
        ...region,
        promotionPlacements: region.promotionPlacements[initiativeId] ?? [],
        lockedPlacements: PromotionPlacementZod.options.filter(
          (placement) => region.promotionPlacementLocks?.[initiativeId]?.[placement]
        ),
      })),
    [initiativeId, regions]
  )

  const { mutateAsync: updatePromotionPlan } = useUpdatePromotionPlan()
  const [pendingWrites, setPendingWrites] = useState(0)

  const onPlacementChange = useCallback<AdminPromotionPlacementsViewProps['onPlacementChange']>(
    async (regionId, placement, inPlan) => {
      setPendingWrites((count) => count + 1)
      try {
        await updatePromotionPlan({ regionId, initiativeId, placement, inPlan, overrideLock: true })
      } catch (error) {
        toast({
          title: intl.formatMessage({ id: 'promotions.placements.error' }),
          variant: 'destructive',
        })
        throw error
      } finally {
        setPendingWrites((count) => count - 1)
      }
    },
    [initiativeId, intl, updatePromotionPlan]
  )

  const { mutateAsync: exportPlans, isPending: isExportPending } = usePromotionPlanExport()

  const onExport = useCallback(async () => {
    try {
      const base64Data = await exportPlans({ initiativeId })
      downloadFromBase64(
        base64Data,
        `options-visibilite-${initiativeId}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    } catch {
      toast({
        title: intl.formatMessage({ id: 'promotions.placements.exportError' }),
        variant: 'destructive',
      })
    }
  }, [exportPlans, initiativeId, intl])

  const onBackToPiloteClicked = useBackToPilote()

  return (
    <>
      <AdminPromotionPlacementsView
        regions={regionsWithPlacements}
        onBackToPiloteClicked={onBackToPiloteClicked}
        onPlacementChange={onPlacementChange}
        onExport={pendingWrites > 0 ? undefined : onExport}
        isExportPending={isExportPending}
      />
      <Toaster />
    </>
  )
}
